// Frontend mirror of the GET /dashboard/overview payload (DASH.A1, #821).
//
// apps/web cannot import apps/api types directly (separate package), so the
// shape is duplicated here — the same pattern DashboardPage uses for its
// ProjectHealth interface. Source of truth:
//   apps/api/src/services/dashboardOverview.service.ts
//
// Every zone is wrapped in ZoneResult<T> (per-zone graceful degradation): on
// failure the zone carries `ok: false` PLUS an empty-shape `data` fallback, so
// callers can always read `.data` without a crash. Read `.data` everywhere;
// surface `ok: false` as a degraded notice — never let one zone break layout.

export type RagStatus = 'GREEN' | 'AMBER' | 'RED';

export type ZoneResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: 'Failed to load'; data: T };

export interface ActiveProject {
  id: string;
  name: string;
  domain: string | null;
  currentPhase: string;
  phaseNumber: number;
  sprintDay: number;
  sprintTotal: number;
  sprintPct: number;
  ptsDone: number;
  lastActivityAt: string;
  healthStatus: RagStatus;
  // PM-only fields — OMITTED entirely from the CLIENT response shape, hence
  // optional here. Treat as possibly-undefined at every read site.
  ptsRemaining?: number;
  spikeCount?: number;
  spikePts?: number;
  blockedCount?: number;
}

export type GateType = 'ENTRY' | 'EXIT' | 'SPRINT_EXIT' | 'CLIENT_SIGNOFF' | 'ADR_ACK';

export interface UpcomingGate {
  gateType: GateType;
  projectId: string;
  projectName: string;
  phaseName: string | null;
  dueAt: string;
  context: string;
}

export interface RecentDecision {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  status: 'PROPOSED' | 'ACCEPTED' | 'DEPRECATED' | 'SUPERSEDED';
  impactLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | null;
  decisionType: 'ARCHITECTURAL' | 'TECHNICAL' | 'PROCESS' | 'PRODUCT' | null;
  createdAt: string;
}

export interface RoleOverviewSlot {
  role: string;
  filled: number;
  open: number;
}

export interface RoleOverview {
  projectId: string;
  projectName: string;
  slots: RoleOverviewSlot[];
  slot_definition_source: 'hardcoded_v1' | 'tenant_config' | 'project_template';
}

export interface NotificationSummaryItem {
  id: string;
  type: string;
  title: string;
  createdAt: string;
  isRead: boolean;
}

export interface NotificationSummary {
  unreadCount: number;
  // DASH.B2 (#824): the preview surfaces up to 5 (was 3). unreadCount is the
  // authoritative badge count and is unchanged.
  last5: NotificationSummaryItem[];
}

export interface SetupStatus {
  setupProjectId: string;
  currentPhase: string;
  nextTask: string;
}

// DASH.B2 (#824) — Action-zone item lists. Mirror of dashboardOverview.service.
// Both are INTERNAL surfaces and arrive as [] for CLIENT role (server strips).
export type AssignedTiming = 'ON_TRACK' | 'RUNNING_LATE' | 'LATE';

// #2762 — sourceType discriminator (also mirrors dashboardOverview.service).
// timing/daysLate are ISSUE-only — OQ/Risk/NFR have no equivalent urgency
// signal today (see #2762's own recon: a fabricated one was rejected for v1).
export type WorkbasketSourceType = 'ISSUE' | 'OPEN_QUESTION' | 'RISK' | 'NFR';

export interface AssignedWorkItem {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  sourceType: WorkbasketSourceType;
  timing?: AssignedTiming;
  daysLate?: number;
  // SL.L4a (#2960) — ISSUE rows from /dashboard/workbasket only: the sprint the
  // issue's backlog item is in; null when unlinked or the item has no sprint.
  sprint?: { id: string; number: number; name: string } | null;
  // #2834 — NFR rows from /dashboard/workbasket only (e.g. 'GAP',
  // 'NEEDS_BUSINESS_DECISION'). Never set on ISSUE rows.
  status?: string;
}

// GET /dashboard/workbasket response (#2762). A separate, dedicated endpoint
// — NOT a zone of DashboardOverview — so it has its own plain shape, no
// ZoneResult wrapper (a 403 for a CLIENT-tier caller is the "wrong audience"
// signal; there is no partial-degradation case to represent).
export interface WorkbasketResponse {
  items: AssignedWorkItem[];
  // R1 + R2 convergent finding — true when the ISSUE portion hit the
  // server's cap exactly (a heuristic, not an exact "N more" count). Lets the
  // page say something rather than silently dropping items past the cap.
  truncated: boolean;
}

export interface StuckItem {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  type: string;
  stuckSince: string;
}

export interface DashboardOverview {
  active_projects: ZoneResult<ActiveProject[]>;
  role_overview: ZoneResult<RoleOverview[]>;
  upcoming_gates: ZoneResult<UpcomingGate[]>;
  recent_decisions: ZoneResult<RecentDecision[]>;
  notification_summary: ZoneResult<NotificationSummary>;
  my_assigned_work: ZoneResult<AssignedWorkItem[]>;
  stuck_items: ZoneResult<StuckItem[]>;
  setup_status: ZoneResult<SetupStatus | null>;
  // pipeline_status is the "Package Knowledge Graph / pipeline" zone — always
  // null until E2E.A3 ships. This is the backing for the PKG "coming soon"
  // surface; there is no AI-cost field (that tile is frontend-only comingSoon).
  pipeline_status: ZoneResult<null>;
  activity_feed_endpoint: string;
}

/** True when any zone failed to load (carries `ok: false`). */
export function hasDegradedZone(o: DashboardOverview): boolean {
  return (
    !o.active_projects.ok ||
    !o.role_overview.ok ||
    !o.upcoming_gates.ok ||
    !o.recent_decisions.ok ||
    !o.notification_summary.ok ||
    !o.my_assigned_work.ok ||
    !o.stuck_items.ok ||
    !o.setup_status.ok ||
    !o.pipeline_status.ok
  );
}

function degraded<T>(data: T): ZoneResult<T> {
  return { ok: false, error: 'Failed to load', data };
}

/**
 * Accept a value only if it is a well-formed ZoneResult ({ ok: boolean, … });
 * otherwise degrade to the empty-data fallback. Guards against a zone that is
 * MISSING (undefined) AND one that is PRESENT BUT MALFORMED (wrong type, or
 * missing `ok`) from a bad proxy/partial body, so downstream readers never see
 * a non-ZoneResult value.
 */
function asZone<T>(value: unknown, emptyData: T): ZoneResult<T> {
  if (
    value !== null &&
    typeof value === 'object' &&
    'ok' in value &&
    typeof (value as { ok: unknown }).ok === 'boolean'
  ) {
    return value as ZoneResult<T>;
  }
  return degraded(emptyData);
}

/**
 * Coerce a raw `/dashboard/overview` response into a guaranteed-complete
 * DashboardOverview. The real backend always returns all seven zones, but a
 * proxy error or partial/malformed body could omit or corrupt a key — every
 * downstream reader then assumes the zone is a ZoneResult. Normalizing here (a
 * missing/malformed zone becomes an `ok: false` empty-data fallback, surfacing
 * as a degraded notice) keeps every consumer crash-proof without scattering
 * optional chaining. The input is intentionally typed loose — we cannot trust
 * the wire shape.
 */
export function normalizeOverview(raw: unknown): DashboardOverview {
  const r = (raw !== null && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    active_projects: asZone<ActiveProject[]>(r.active_projects, []),
    role_overview: asZone<RoleOverview[]>(r.role_overview, []),
    upcoming_gates: asZone<UpcomingGate[]>(r.upcoming_gates, []),
    recent_decisions: asZone<RecentDecision[]>(r.recent_decisions, []),
    notification_summary: asZone<NotificationSummary>(r.notification_summary, { unreadCount: 0, last5: [] }),
    my_assigned_work: asZone<AssignedWorkItem[]>(r.my_assigned_work, []),
    stuck_items: asZone<StuckItem[]>(r.stuck_items, []),
    setup_status: asZone<SetupStatus | null>(r.setup_status, null),
    pipeline_status: asZone<null>(r.pipeline_status, null),
    activity_feed_endpoint:
      typeof r.activity_feed_endpoint === 'string' ? r.activity_feed_endpoint : '/dashboard/activity',
  };
}
